import { supabase } from '@/lib/supabase';

export type BenefitKind = 'absolute' | 'percent' | 'amount_off' | 'free';
export type BenefitDuration = 'fixed_term' | 'billing_cycles' | 'until_cancelled';
export type Price = {
  catalog_price_agorot: number; subtotal_agorot: number; vat_agorot: number;
  total_agorot: number; vat_percent: number; benefit_amount_agorot: number;
  addons_total_agorot: number; billing_cycle: string; plan_code: string;
};
export type BenefitInput = {
  benefit_type: BenefitKind; benefit_value: string | null;
  starts_on: string; duration_mode: BenefitDuration; ends_on: string | null;
  billing_cycles: number | null; reason_customer: string; reason_internal: string;
};
export type OrgPriceBenefit = {
  id: string; organization_id: string; benefit_type: string; benefit_value: string | null;
  display_type: string; amount_off_agorot: number | null; applies_to: string;
  starts_at: string; ends_at: string | null; duration_mode: string; billing_cycles: number | null;
  cycles_used: number; status: string; reason_customer: string; reason_internal: string;
  created_at: string; updated_at: string; can_end: boolean;
  return_at: string | null; return_price: Price | null;
};
export type BenefitOverview = {
  organization_id: string; organization_name: string; plan_name: string;
  price: Price; next_charge_at: string; benefits: OrgPriceBenefit[];
};
export type BenefitPreview = {
  price: Price; priced_at: string; fingerprint: string;
  conflicts: Array<{ id: string; reason_customer: string; starts_at: string; ends_at: string | null }>;
  candidate_applies_at_charge: boolean; stored_benefit_type: string;
  stored_benefit_value: string | null; starts_at: string; ends_at: string | null;
};
export type BenefitSaveResult = { benefit: OrgPriceBenefit; price: Price; priced_at: string };
export type BenefitsApi = {
  list(orgId: string): Promise<BenefitOverview>;
  preview(orgId: string, input: BenefitInput): Promise<BenefitPreview>;
  save(orgId: string, input: BenefitInput, fingerprint: string, replaceIds: string[]): Promise<BenefitSaveResult>;
  end(orgId: string, benefitId: string): Promise<unknown>;
};

async function call<T>(name: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw error;
  return data as T;
}

export const orgPriceBenefitsApi: BenefitsApi = {
  list: orgId => call('admin_get_org_price_benefits', { p_org_id: orgId }),
  preview: (orgId, input) => call('admin_preview_org_price_benefit', { p_org_id: orgId, p_input: input }),
  save: (orgId, input, fingerprint, replaceIds) => call('admin_save_org_price_benefit', {
    p_org_id: orgId, p_input: input, p_expected_fingerprint: fingerprint, p_replace_ids: replaceIds,
  }),
  end: (orgId, benefitId) => call('admin_end_org_price_benefit', { p_org_id: orgId, p_benefit_id: benefitId }),
};

/** Unit conversion only. Price, discounts and VAT always come from the server engine. */
export function amountToAgorot(value: string): string | null {
  const match = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(value.trim());
  if (!match) return null;
  const cents = BigInt(match[1]) * 100n + BigInt((match[2] ?? '').padEnd(2, '0'));
  return cents <= 2147483647n ? cents.toString() : null;
}

export function jerusalemToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const part = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function isBenefitForbidden(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'code' in error && error.code === '42501';
}

export function benefitError(error: unknown): string {
  const code = typeof error === 'object' && error !== null && 'message' in error ? String(error.message) : '';
  if (/preview_stale|price_changed|overlap_confirmation/.test(code)) return 'המחיר או ההטבות השתנו. יש לבדוק שוב את התצוגה המקדימה.';
  if (/amount_off_exceeds_catalog/.test(code)) return 'סכום ההנחה צריך להיות חיובי ולא לעלות על מחיר המחירון.';
  if (/end_before_start/.test(code)) return 'תאריך הסיום חייב להיות ביום ההתחלה או אחריו.';
  if (/active_jumpstart_subscription_not_found/.test(code)) return 'לא נמצא מנוי JumpStart פעיל לארגון.';
  return 'לא ניתן להשלים את הפעולה. נסו שוב.';
}
